// @flow
import React, { type Node } from 'react';
import stylesheet from './ActionsMenu.scss';
import { Button, Actions100, ArrowRight100 } from 'shared/legacy';
import classnames from 'classnames';

type itemType = { text: string, link?: string, onClickFunction: Function };

type itemTypes = {
  nextSteps?: Array<itemType>,
  actions?: Array<itemType>,
};

type Props = {
  /** Takes an object in the form of: {
   * nextSteps: [{text: 'some action description', link: 'link to do action', onClickFunction: () => {}}],
   * actions: [{text: 'some action description', link: 'link to do action', onClickFunction: () => {}}],
   * } */
  items: itemTypes,
  showTitle: boolean,
};

type State = {
  isMenuShowing: boolean,
};

type ButtonRef = ?HTMLDivElement;
type WrapperRef = ?HTMLDivElement;

class ActionsMenu extends React.Component<Props, State> {
  handleButtonClick: Function;
  handleClick: Function;
  handleClickInside: Function;
  handleClickOutside: Function;
  setWrapperRef: Function;
  setButtonRef: Function;

  buttonRef: ButtonRef;
  wrapperRef: WrapperRef;

  state = {
    isMenuShowing: false,
  };

  componentDidMount() {
    document.addEventListener('click', this.handleClick, true);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClick, true);
  }

  handleClickOutside = () => {
    this.setState({
      isMenuShowing: false,
    });
  };

  handleClickInside = () => {
    if (!this.state.isMenuShowing) {
      this.setState({
        isMenuShowing: true,
      });
    }
  };

  handleClick = (evt: MouseEvent) => {
    const isClickingInside =
      this.wrapperRef &&
      evt.target instanceof HTMLElement &&
      this.wrapperRef.contains(evt.target);

    const isClickingButton =
      this.buttonRef &&
      evt.target instanceof HTMLElement &&
      this.buttonRef.contains(evt.target);

    if (isClickingButton) return;
    if (isClickingInside) {
      this.handleClickInside();
      return;
    }
    this.handleClickOutside();
  };

  handleButtonClick = () => {
    this.setState(prevState => ({
      isMenuShowing: !prevState.isMenuShowing,
    }));
  };

  setWrapperRef = (ref: WrapperRef) => {
    this.wrapperRef = ref;
  };

  setButtonRef = (ref: ButtonRef) => {
    this.buttonRef = ref;
  };

  RenderBodyContainer = ({
    variant,
    children,
    showTitle = true,
  }: {
    variant: 'next-steps' | 'actions',
    children: Node,
    showTitle: boolean,
  }) => (
    <div className={stylesheet[`actions-menu__${variant}-container`]}>
      {showTitle && (
        <p className={stylesheet['actions-menu__container-subheading']}>
          {variant
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')}
        </p>
      )}
      {children}
    </div>
  );

  RenderActionsContainer = () => {
    const { nextSteps, actions } = this.props.items;
    const { showTitle } = this.props;
    const getActionItemClass = variant =>
      classnames([
        stylesheet['actions-menu__item'],
        stylesheet[`actions-menu__${variant}-item`],
      ]);

    const { RenderBodyContainer } = this;

    return (
      <div className={stylesheet['actions-menu__container']}>
        {nextSteps && nextSteps.length && (
          <RenderBodyContainer variant="next-steps" showTitle={showTitle}>
            {nextSteps.map((step, i) =>
              step.onClickFunction ? (
                <div
                  key={`${i}-${step.text}`}
                  className={stylesheet['actions-menu__next-step-button']}
                >
                  <Button
                    as="no-styling"
                    onClick={e => step.onClickFunction(e)}
                  >
                    <ArrowRight100 />
                    <span
                      className={stylesheet['actions-menu__next-step-inner']}
                    >
                      {step.text}
                    </span>
                  </Button>
                </div>
              ) : (
                <a
                  key={`${i}-${step.text}`}
                  className={getActionItemClass('next-step')}
                  href={step.link}
                >
                  <ArrowRight100 />
                  <span className={stylesheet['actions-menu__next-step-inner']}>
                    {step.text}
                  </span>
                </a>
              )
            )}
          </RenderBodyContainer>
        )}
        {actions && actions.length && (
          <RenderBodyContainer variant="actions" showTitle={showTitle}>
            {actions.map((action, i) =>
              action.onClickFunction ? (
                <div
                  key={`${i}-${action.text}`}
                  className={stylesheet['actions-menu__actions-button']}
                >
                  <Button
                    as="no-styling"
                    onClick={e => action.onClickFunction(e)}
                  >
                    <span className={stylesheet['actions-menu__actions-inner']}>
                      {action.text}
                    </span>
                  </Button>
                </div>
              ) : (
                <a
                  key={`${i}-${action.text}`}
                  className={getActionItemClass('action')}
                  href={action.link}
                >
                  {action.text}
                </a>
              )
            )}
          </RenderBodyContainer>
        )}
      </div>
    );
  };

  render() {
    const { RenderActionsContainer } = this;
    return (
      <div
        className={stylesheet['actions-menu__wrapper']}
        ref={this.setWrapperRef}
      >
        <div
          className={stylesheet['actions-menu__button-wrapper']}
          ref={this.setButtonRef}
        >
          <Button as="no-styling" onClick={this.handleButtonClick}>
            <Actions100 size="ty-e" />
          </Button>
        </div>
        {this.state.isMenuShowing && <RenderActionsContainer />}
      </div>
    );
  }
}

export default ActionsMenu;
